import { v4 as uuidv4 } from 'uuid'
import utils from '~/components/tools/utils'
import { isAccessLogsUrl } from '~/helpers/access-logs'

const SERIALIZED_HEADER_ALLOWLIST = ['Accept', 'Content-Type', 'X-Request-Id']

function getRequestUrl (config = {}) {
  const url = config.url || ''
  if (url.startsWith('http')) {
    return url
  }

  return `${config.baseURL || ''}${url}`
}

function sanitizeHeaders (headers = {}) {
  return SERIALIZED_HEADER_ALLOWLIST.reduce((sanitized, headerName) => {
    const value = headers[headerName] || headers[headerName.toLowerCase()]
    if (value) {
      sanitized[headerName] = value
    }
    return sanitized
  }, {})
}

function shouldSuppressGlobalError (error) {
  return Boolean(
    error?.config?.meta?.suppressGlobalError ||
    isAccessLogsUrl(error?.config?.url) ||
    isAccessLogsUrl(getRequestUrl(error?.config))
  )
}

class SerializableAxiosError extends Error {
  constructor (axiosError) {
    const config = axiosError.config || {}
    const url = getRequestUrl(config)
    const response =
      axiosError.response === undefined
        ? null
        : {
            status: axiosError.response.status,
            data: axiosError.response.data
          }
    const message = safeStringify({
      message: axiosError.message,
      method: (config.method || '').toUpperCase(),
      url,
      headers: sanitizeHeaders(config.headers),
      response
    })
    super(message)
    this.name = 'SerializableAxiosError'
    this.request = Boolean(axiosError.request)
    this.response = response
    this.url = url
    this.method = (config.method || '').toUpperCase()
    this.cm24SuppressReporting = shouldSuppressGlobalError(axiosError)
  }
}

function safeStringify (value) {
  const seen = new WeakSet()

  return JSON.stringify(value, (key, val) => {
    if (val && typeof val === 'object') {
      if (seen.has(val)) {
        return '[Circular]'
      }
      seen.add(val)
    }

    return val
  })
}

export default function ({
  $axios,
  $config,
  app,
  $cookies
}) {
  $axios.interceptors.request.use((x) => {
    // to avoid overwriting if another interceptor
    // already defined the same object (meta)
    x.meta = x.meta || {}
    x.meta.requestStartedAt = new Date().getTime()

    return x
  })

  $axios.interceptors.response.use((x) => {
    x.responseTime = new Date().getTime() - x.config.meta.requestStartedAt
    x.requestStartedAt = x.config.meta.requestStartedAt
    return x
  })

  $axios.onRequest((config) => {
    // console.log(`[AXIOS]  ${config.method.toUpperCase()} ${config.url}`)

    const sid = $cookies.get('X-Session-Id')
    if (sid) {
      config.headers['X-Session-Id'] = sid
    }
    config.headers['X-Request-Id'] = uuidv4()

    const { locale } = app.i18n
    let domain = locale
    if (locale === 'en') {
      domain = 'com'
    } else if (locale === 'da') {
      domain = 'dk'
    } else if (locale === 'sv') {
      domain = 'se'
    } else if (locale === 'cs') {
      domain = 'cz'
    }

    const apiBaseURL = $config.apiBaseURL
    const baseUrl = utils.getI18Domain(apiBaseURL, domain)

    config.baseURL = baseUrl

    return config
  })

  $axios.onError((error) => {
    if (shouldSuppressGlobalError(error)) {
      error.cm24SuppressReporting = true
      return Promise.reject(error)
    }

    const rejectionReason = error?.isAxiosError
      ? new SerializableAxiosError(error)
      : error

    return Promise.reject(rejectionReason)
  })
}
